import { useEffect, useState } from 'react';
import {
  RECIPE_COURSES,
  formatCourse,
  type ApercuRegleResponse,
  type RecipeCourse,
  type TagRule,
  type TagRuleCriteres,
} from '@mlc/shared';
import { apercuTagRule, createTagRule, listCuisines } from './api';
import { Banner, Button, Select, TextInput } from './ui';

/**
 * Composer une étiquette calculée (US-62).
 *
 * ### Le vocabulaire est fermé, et l'écran le montre
 *
 * On choisit un critère dans une liste, on lui donne une valeur, on
 * recommence. Il n'y a **ni « ou », ni parenthèses, ni négation imbriquée** :
 * les critères se cumulent par ET, et la négation n'existe que là où le
 * vocabulaire la propose (« ne contient pas », « sans four »). Un langage
 * d'expression serait un moteur de requêtes déguisé — s'il faut un « ou »,
 * c'est deux étiquettes.
 *
 * ### La portée n'est pas un choix
 *
 * Elle se déduit des critères : un seul critère de journal — « jamais
 * réalisée », « pas faite depuis N jours », les notes — et l'étiquette devient
 * personnelle, chacun voyant la sienne. L'écran l'annonce **avant**
 * l'enregistrement, parce que c'est contre-intuitif et qu'une surprise ici se
 * paierait en étiquettes fausses pour toute la famille.
 *
 * ### L'aperçu
 *
 * « Cette règle poserait l'étiquette sur 7 recettes », avec quelques titres.
 * Calculé par le serveur **dans le périmètre visible du demandeur** : un
 * compteur bâti sur toute la base dirait combien de recettes cachées la
 * vérifient.
 */

interface DefinitionCritere {
  cle: keyof TagRuleCriteres;
  libelle: string;
  /**
   * `nombre` : un seuil ; `oui` : un drapeau ; `texte` : un mot ; `liste` : des
   * mots séparés par des virgules ; `course` : un choix parmi les six natures
   * de plat ; `cuisine` : un mot, avec l'autocomplétion des cuisines déjà
   * employées.
   *
   * **Les deux dernières étaient des saisies libres**, et c'est ce qui rendait
   * ces deux critères inutilisables (défaut relevé le 24 août 2026) : on tapait
   * « Plat » là où la donnée vaut `plat`, « italienne » là où elle vaut
   * `italien`. Un champ qui accepte une valeur ne correspondant à rien est un
   * champ qui promet un résultat qu'il ne peut pas donner — et l'aperçu
   * répondait « aucune recette », ce qui était juste et incompréhensible.
   */
  saisie: 'nombre' | 'oui' | 'texte' | 'liste' | 'course' | 'cuisine';
  suffixe?: string;
  journal?: boolean;
}

/** Les quinze familles de critères, dans l'ordre où on les cherche. */
const CRITERES: readonly DefinitionCritere[] = [
  { cle: 'temps_total_max', libelle: 'Préparation + cuisson au plus', saisie: 'nombre', suffixe: 'min' },
  { cle: 'temps_total_min', libelle: 'Préparation + cuisson au moins', saisie: 'nombre', suffixe: 'min' },
  { cle: 'repos_max', libelle: 'Temps de repos au plus', saisie: 'nombre', suffixe: 'min' },
  { cle: 'repos_min', libelle: 'Temps de repos au moins', saisie: 'nombre', suffixe: 'min' },
  { cle: 'sans_four', libelle: 'N’utilise pas le four', saisie: 'oui' },
  { cle: 'avec_four', libelle: 'Utilise le four', saisie: 'oui' },
  { cle: 'difficulte_max', libelle: 'Difficulté au plus (1 à 3)', saisie: 'nombre' },
  { cle: 'difficulte_min', libelle: 'Difficulté au moins (1 à 3)', saisie: 'nombre' },
  { cle: 'nb_ingredients_max', libelle: 'Nombre d’ingrédients au plus', saisie: 'nombre' },
  { cle: 'nb_ingredients_min', libelle: 'Nombre d’ingrédients au moins', saisie: 'nombre' },
  { cle: 'contient', libelle: 'Contient (séparés par des virgules)', saisie: 'liste' },
  { cle: 'ne_contient_pas', libelle: 'Ne contient pas (séparés par des virgules)', saisie: 'liste' },
  { cle: 'course', libelle: 'Nature du plat', saisie: 'course' },
  { cle: 'cuisine', libelle: 'Cuisine', saisie: 'cuisine' },
  // US-74. Les deux critères de la règle « procédé en vidéo », séparés parce
  // qu'ils disent deux choses différentes : ce qui manque, et d'où ça vient.
  { cle: 'sans_etapes', libelle: 'N’a aucune étape enregistrée', saisie: 'oui' },
  { cle: 'source_video', libelle: 'Vient d’une vidéo', saisie: 'oui' },
  { cle: 'jamais_realisee', libelle: 'Jamais réalisée', saisie: 'oui', journal: true },
  { cle: 'derniere_avant_jours', libelle: 'Pas faite depuis plus de', saisie: 'nombre', suffixe: 'jours', journal: true },
  { cle: 'note_derniere_min', libelle: 'Dernière note au moins', saisie: 'nombre', suffixe: '/5', journal: true },
  { cle: 'note_derniere_max', libelle: 'Dernière note au plus', saisie: 'nombre', suffixe: '/5', journal: true },
  { cle: 'note_moyenne_min', libelle: 'Moyenne des notes au moins', saisie: 'nombre', suffixe: '/5', journal: true },
  { cle: 'note_moyenne_max', libelle: 'Moyenne des notes au plus', saisie: 'nombre', suffixe: '/5', journal: true },
  { cle: 'realisee_au_moins', libelle: 'Réalisée au moins', saisie: 'nombre', suffixe: 'fois', journal: true },
];

/**
 * Ce qu'affiche la puce d'un critère posé.
 *
 * `course` est une valeur d'énumération : elle ne s'affiche **jamais brute**
 * (§ `formatCourse`). C'est aussi ce qui rendait le défaut du 24 août
 * indétectable à l'œil — la puce disait « Plat », donc tout paraissait normal,
 * alors que la valeur envoyée au serveur était bien celle qu'on avait tapée.
 */
function valeurAffichee(critere: DefinitionCritere, criteres: TagRuleCriteres): string {
  const valeur = criteres[critere.cle];
  if (critere.saisie === 'course') return formatCourse(valeur as RecipeCourse) ?? String(valeur);
  return String(valeur);
}

export interface TagRuleEditorProps {
  /** Une règle vient d'être créée : la liste doit la reprendre. */
  onCreated: (regle: TagRule) => void;
}

export function TagRuleEditor({ onCreated }: TagRuleEditorProps): React.JSX.Element {
  const [ouvert, setOuvert] = useState(false);
  const [label, setLabel] = useState('');
  const [criteres, setCriteres] = useState<TagRuleCriteres>({});
  const [choix, setChoix] = useState<string>('');
  const [valeur, setValeur] = useState('');
  const [apercu, setApercu] = useState<ApercuRegleResponse | null>(null);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);
  /**
   * Les cuisines déjà employées, pour l'autocomplétion — même vivier que le
   * formulaire de recette (US-55), filtré par la clause de visibilité côté
   * serveur. Une cuisine qui n'existerait que dans une recette secrète n'y est
   * pas (US-46, vecteur 4).
   *
   * Une lecture qui échoue laisse le champ libre : on perd la suggestion, pas
   * la saisie.
   */
  const [cuisinesConnues, setCuisinesConnues] = useState<string[]>([]);

  useEffect(() => {
    if (!ouvert) return;
    let vivant = true;
    listCuisines()
      .then((liste) => { if (vivant) setCuisinesConnues(liste); })
      .catch(() => { /* le champ reste libre */ });
    return () => { vivant = false; };
  }, [ouvert]);

  const posees = CRITERES.filter((critere) => criteres[critere.cle] !== undefined);
  const personnelle = posees.some((critere) => critere.journal === true);
  const definition = CRITERES.find((critere) => critere.cle === choix) ?? null;

  function ajouter(): void {
    if (definition === null) return;
    const valeurPropre = valeur.trim();
    let posee: unknown;
    if (definition.saisie === 'oui') posee = true;
    else if (definition.saisie === 'nombre') {
      const nombre = Number(valeurPropre);
      if (valeurPropre === '' || !Number.isFinite(nombre)) return;
      posee = nombre;
    } else if (definition.saisie === 'liste') {
      const mots = valeurPropre.split(',').map((mot) => mot.trim()).filter((mot) => mot !== '');
      if (mots.length === 0) return;
      posee = mots;
    } else {
      if (valeurPropre === '') return;
      posee = valeurPropre;
    }
    setCriteres((actuels) => ({ ...actuels, [definition.cle]: posee }));
    setChoix('');
    setValeur('');
    setApercu(null);
  }

  function retirer(cle: keyof TagRuleCriteres): void {
    setCriteres((actuels) => {
      const copie = { ...actuels };
      delete copie[cle];
      return copie;
    });
    setApercu(null);
  }

  function voirLAperçu(): void {
    setEnCours(true);
    setErreur(null);
    apercuTagRule(criteres)
      .then(setApercu)
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(false); });
  }

  function enregistrer(): void {
    setEnCours(true);
    setErreur(null);
    createTagRule({ label: label.trim(), criteres })
      .then((creee) => {
        onCreated(creee);
        setOuvert(false);
        setLabel('');
        setCriteres({});
        setApercu(null);
      })
      .catch((e: unknown) => { setErreur(e instanceof Error ? e.message : String(e)); })
      .finally(() => { setEnCours(false); });
  }

  if (!ouvert) {
    return (
      <Button type="button" variant="secondary" onClick={() => { setOuvert(true); }}>
        Composer une étiquette
      </Button>
    );
  }

  return (
    <section
      data-testid="tag-rule-editor"
      aria-labelledby="titre-nouvelle-regle"
      className="flex flex-col gap-3 rounded-card border border-border p-3"
    >
      <h4 id="titre-nouvelle-regle" className="text-heading-3">Nouvelle étiquette calculée</h4>

      {erreur !== null && <Banner tone="danger">{erreur}</Banner>}

      <label className="flex flex-col gap-1 text-sm">
        Nom de l’étiquette
        <TextInput value={label} onChange={(e) => { setLabel(e.target.value); }} className="w-56" />
      </label>

      {posees.length > 0 && (
        <ul className="flex flex-col gap-1">
          {posees.map((critere) => (
            <li key={critere.cle} className="flex flex-wrap items-center gap-2 text-sm">
              <span>
                {critere.libelle}
                {critere.saisie === 'oui' ? '' : ` : ${valeurAffichee(critere, criteres)}`}
                {critere.suffixe === undefined ? '' : ` ${critere.suffixe}`}
              </span>
              <Button type="button" variant="ghost" size="compact" onClick={() => { retirer(critere.cle); }}>
                Retirer
              </Button>
            </li>
          ))}
        </ul>
      )}

      {/* Les critères se cumulent par ET, et l'écran le dit plutôt que de
          laisser deviner : « moins de 20 min » **et** « sans four ». */}
      <div className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col gap-1 text-sm">
          Ajouter un critère
          <Select
            aria-label="Critère"
            value={choix}
            onChange={(e) => { setChoix(e.target.value); setValeur(''); }}
          >
            <option value="">—</option>
            {CRITERES.filter((critere) => criteres[critere.cle] === undefined).map((critere) => (
              <option key={critere.cle} value={critere.cle}>{critere.libelle}</option>
            ))}
          </Select>
        </label>
        {/* **La nature du plat est un choix, pas une saisie** : six valeurs
            fermées, et la valeur envoyée est celle de l'énumération, jamais son
            libellé. C'est la moitié « écran » du défaut du 24 août — l'autre
            moitié, le repli à la comparaison, vit dans `computeAutoTags.ts` et
            répare en plus les règles déjà enregistrées. */}
        {definition !== null && definition.saisie === 'course' && (
          <label className="flex flex-col gap-1 text-sm">
            Valeur
            <Select
              aria-label="Valeur du critère"
              value={valeur}
              onChange={(e) => { setValeur(e.target.value); }}
            >
              <option value="">—</option>
              {RECIPE_COURSES.map((nature) => (
                <option key={nature} value={nature}>{formatCourse(nature) ?? nature}</option>
              ))}
            </Select>
          </label>
        )}
        {definition !== null && definition.saisie !== 'oui' && definition.saisie !== 'course' && (
          <label className="flex flex-col gap-1 text-sm">
            Valeur
            <TextInput
              aria-label="Valeur du critère"
              value={valeur}
              onChange={(e) => { setValeur(e.target.value); }}
              className="w-40"
              // La cuisine reste un champ libre — le monde est plus grand
              // qu'une liste fermée (§2) — mais il propose celles qu'on emploie
              // déjà : une règle écrite sur une cuisine que personne n'a jamais
              // saisie ne poserait jamais rien.
              {...(definition.saisie === 'cuisine' ? { list: 'mlc-cuisines-regle' } : {})}
            />
            {definition.saisie === 'cuisine' && (
              <datalist id="mlc-cuisines-regle">
                {cuisinesConnues.map((nom) => <option key={nom} value={nom} />)}
              </datalist>
            )}
          </label>
        )}
        <Button type="button" variant="secondary" size="compact" disabled={definition === null} onClick={ajouter}>
          Ajouter
        </Button>
      </div>

      {posees.length > 1 && (
        <p className="text-meta">
          Les critères se cumulent : une recette doit les vérifier tous.
        </p>
      )}

      {/* La portée est déduite, jamais choisie — et dite avant d'enregistrer. */}
      {posees.length > 0 && (
        <p className="text-sm text-foreground-secondary">
          {personnelle
            ? 'Cette étiquette sera calculée sur votre journal : chacun verra la sienne.'
            : 'Cette étiquette décrit la recette : elle sera la même pour tout le monde.'}
        </p>
      )}

      {apercu !== null && (
        <Banner tone="info">
          {apercu.total === 0
            ? 'Aucune recette ne vérifie cette règle pour l’instant.'
            : `Cette règle poserait l’étiquette sur ${String(apercu.total)} recette${apercu.total > 1 ? 's' : ''}${
              apercu.titres.length > 0 ? ` : ${apercu.titres.join(', ')}` : ''}.`}
        </Banner>
      )}

      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          variant="secondary"
          disabled={enCours || posees.length === 0}
          onClick={voirLAperçu}
        >
          Voir ce que ça donnerait
        </Button>
        <Button
          type="button"
          variant="primary"
          disabled={enCours || posees.length === 0 || label.trim() === ''}
          onClick={enregistrer}
        >
          Créer l’étiquette
        </Button>
        <Button type="button" variant="ghost" onClick={() => { setOuvert(false); }}>Annuler</Button>
      </div>
    </section>
  );
}
